import {SoiRegistrationConfig, TrunkingSite} from "../../app/types";
import {FormSection} from "../../common/components/SectionedForm";
import {useTranslation} from "react-i18next";
import React, {ReactElement, ReactNode, useCallback, useState} from "react";
import {api} from "../../common/api";
import {useFetchData} from "../../common/servercall/useFetchData";
import {LabelValues, LabelValueType} from "../../common/components/LabelValues";
import {ModeEdit, Settings} from "@mui/icons-material";
import PaperStack from "../../common/form/PaperStack";
import FormGroup from "../../common/form/FormGroup";
import axios from "axios";
import SyncLockIcon from '@mui/icons-material/SyncLock';
import {FabButton} from "../../common/components/FabButton";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import {format, parseISO} from "date-fns";
import {Box, Flex} from "rebass";
import {IconButton} from "@mui/material";
import {EmailDialog} from "./EmailDialog";

export const useSoiRegistrationConfigSection = (trunkingSite: TrunkingSite | undefined, readOnly: boolean) : FormSection<SoiRegistrationConfig> => {
    const { t } = useTranslation()
    const [registrationConfig, setRegistrationConfig] = React.useState<SoiRegistrationConfig | null>(null)
    const [emailToUpdate, setEmailToUpdate] = useState<SoiRegistrationConfig | null>(null)

    const fetchConfig = useCallback(
        async (productId: string | undefined): Promise<SoiRegistrationConfig | undefined> => {
            if (!productId) return
            const { data } = await api.get<SoiRegistrationConfig>(`/api/trunking/site/${productId}/soi-registration-config`)
            return data
        },
        []
    )
    const { data, isFetching, error, refetch } = useFetchData<SoiRegistrationConfig>(
        useCallback(() => fetchConfig(trunkingSite?.productId), [fetchConfig, trunkingSite?.productId])
    )

    const sipLabelValues = useCallback(
        (value: SoiRegistrationConfig): LabelValueType[] => {
            return [
                { label: t('trunking-sites.sip-authentication-username'), value: <>{value.sipauthenticationUsername}</> },
                { label: t('trunking-sites.sip-outbound-proxy'), value: <>{value.sipoutboundProxy}</> },
                { label: t('trunking-sites.sip-server-domain'), value: <>{value.sipserverDomain}</> },
                { label: t('trunking-sites.send-continuous-options-message'), value: <>{value.sendContinuousOptionsMessage ? t('general.enabled') : t('general.disabled')}</> },
            ]}, [t]
    )

    const emailValue = useCallback(
        (value: SoiRegistrationConfig): ReactElement => (
            <Flex alignItems='center' justifyContent={'flex-start'} flexDirection={'row'}>
                <Box>
                    {value.email}
                </Box>
                {!readOnly && (<Box>
                    <IconButton size={'small'} color='secondary' aria-label='Change user' component='span'
                                onClick={() => setEmailToUpdate(value)}>
                        <ModeEdit />
                    </IconButton>
                </Box>)}

            </Flex>
        ),
        [setEmailToUpdate, readOnly],
    )

    const credentialLabelValues = useCallback(
        (value: SoiRegistrationConfig): LabelValueType[] => {
            return [
                { label: t('trunking-sites.e-mail'), value: emailValue(value) },
                { label: t('trunking-sites.credential-email-date'), value: <>{value.credentialEmailDate ? format(parseISO(value?.credentialEmailDate, {additionalDigits: 2}), 'dd/MM/yyyy HH:mm') : ''}</> },
                { label: '', value: <FabButton disabled={!value.email}
                                               label={t('trunking-sites.request-credentials')}
                                               onClick={() => {setRegistrationConfig(value)}}
                                               icon={() => <SyncLockIcon/>} />
                },
            ]}, [t, setRegistrationConfig, emailValue]
    )

    const requestNewCredentials = useCallback(async () => {
        await api.put(`/api/trunking/site/${trunkingSite?.productId}/soi-registration-credentials`, {})
        setRegistrationConfig(null)
        await refetch()
    }, [trunkingSite?.productId, refetch])

    const renderSoiRegistrationConfig = useCallback(
        (soiRegistrationConfig: SoiRegistrationConfig): ReactNode => {
            return (
                <PaperStack>
                    <FormGroup label={t('trunking-sites.sip')} key={1} fullWidth={true}>
                        <LabelValues labelValues={sipLabelValues(soiRegistrationConfig)} />
                    </FormGroup>
                    <FormGroup label={t('trunking-sites.credentials')} key={2} fullWidth={true}>
                        <LabelValues labelValues={credentialLabelValues(soiRegistrationConfig)} />
                    </FormGroup>
                    <ConfirmationModal<SoiRegistrationConfig | null>
                        title={t('trunking-sites.credentials')}
                        text={item => t('trunking-sites.credentials-model.confirmation-text', {email: registrationConfig?.email})}
                        buttonLabel={t('trunking-sites.credentials-model.confirm-button')}
                        item={registrationConfig}
                        action={requestNewCredentials}
                        onClose={() => setRegistrationConfig(null)}
                        onSuccess={() => setRegistrationConfig(null)}
                    />
                    {emailToUpdate ? <EmailDialog trunkingSite={trunkingSite} onSuccess={refetch} email={emailToUpdate}
                                           handleClose={() => setEmailToUpdate(null)} /> : <></>}
                </PaperStack>
            )
        },
        [sipLabelValues, credentialLabelValues, t, setRegistrationConfig, registrationConfig, requestNewCredentials, trunkingSite, refetch, emailToUpdate]
    )
    return {
        title: t('trunking-sites.section.configuration'),
        data: data,
        isFetching: isFetching,
        error: !!error ?axios.isAxiosError(error) && error.response?.status === 404? `trunking-sites.no-trunk-defined` : error : null,
        refetch: refetch,
        renderDetails: renderSoiRegistrationConfig,
        renderForm: null,
        enabled: () => true,
        icon: () => <Settings/>
    }
}